/**
 * ConditionEditorRenderer
 *
 * JSONForms custom renderer that wraps the ConditionEditor (RAQB) component
 * for editing business rule conditions in the BusinessRuleDetailView.
 *
 * Features:
 * - Field autocomplete with code completion
 * - Field-to-field comparison (e.g., Order.status == Order.oldStatus)
 * - Related entities in scope (1 level deep)
 * - Old/new value comparison via oldStatus field
 */

import { useMemo, lazy, Suspense } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { CACHE_TIME } from '@/config/queryDefaults';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ruleService } from '@/services/core/RuleService';
import type { ConditionDto } from '@/types/core/Rules';

// Lazy-load the RAQB-based editor so react-awesome-query-builder (~525 kB)
// splits into its own chunk and is fetched only when a condition field
// actually renders (business-rule editing) — not on every form view, which
// all import this renderer via the shared customRenderers registry.
const ConditionEditor = lazy(() =>
  import('@/core/rules/components/ConditionEditor').then((m) => ({ default: m.ConditionEditor }))
);

/**
 * Tester for the ConditionEditorRenderer
 * Matches the #/properties/when scope with high priority
 */
export const conditionEditorTester = rankWith(
  15, // High priority to override default JSONB renderer
  (uischema) => {
    return isControl(uischema) && uischema.scope === '#/properties/when';
  }
);

/**
 * ConditionEditorRenderer Component
 * Integrates the RAQB-based ConditionEditor into JSONForms
 */
const ConditionEditorRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, errors } = props;
  const { t } = useTranslation();
  const ctx = useJsonForms();
  const formData = ctx.core?.data || {};

  // Get the entity and module from form data
  const entityName = formData.entity;
  const moduleName = formData.module;
  const orgUnitId = formData.orgUnitId;

  // Fetch context schema - includes related entities, oldStatus, aggregations
  const {
    data: contextSchema,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['context-schema', entityName, orgUnitId],
    queryFn: () => ruleService.getContextSchema(entityName, orgUnitId),
    enabled: !!entityName,
    staleTime: CACHE_TIME.METADATA,
  });

  // Parse JSON string/object to ConditionDto
  const parsedValue: ConditionDto | null = useMemo(() => {
    if (!data) return null;
    try {
      return typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
      console.warn('[ConditionEditorRenderer] Failed to parse condition JSON:', data);
      return null;
    }
  }, [data]);

  // Convert back to JSON string for form (schema expects string type)
  const handleConditionChange = (condition: ConditionDto | null) => {
    // Stringify the condition object - schema type is ["string", "null"]
    const jsonString = condition ? JSON.stringify(condition) : null;
    handleChange(path, jsonString);
  };

  // Show info message when no entity selected
  if (!entityName) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">
          Select a module and entity above to configure conditions.
        </Alert>
      </Box>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">Loading context schema...</Alert>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load context schema for {moduleName}.{entityName}
        </Alert>
      </Box>
    );
  }

  // No context schema available
  if (!contextSchema) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="warning">
          No context schema available for {moduleName}.{entityName}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {/* The condition is the INVARIANT, not the trigger: a ValidationError fires when it does not
          hold, a SetDecision when it does. First-time authors reach for "Has no value" to mean
          "block when empty" and get the opposite — say it where the choice is made. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('raqb.conditionSemantics')}
      </Typography>
      <Suspense fallback={<Alert severity="info">Loading editor…</Alert>}>
        <ConditionEditor
          value={parsedValue}
          onChange={handleConditionChange}
          contextSchema={contextSchema}
          error={errors}
          readOnly={false}
          expression={formData.expression}
        />
      </Suspense>
    </Box>
  );
};

/**
 * Connected renderer with JSON Forms props
 */
export const ConditionEditorRenderer = withJsonFormsControlProps(
  ConditionEditorRendererComponent
);

/**
 * Renderer entry for registration in customRenderers array
 */
export const conditionEditorRendererEntry = {
  tester: conditionEditorTester,
  renderer: ConditionEditorRenderer,
};
